

export const dynamic = 'force-dynamic';

import { useState } from "react";
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import DashboardLayout from "@/app/components/DashboardLayout";
import { useAuth } from "@/hooks/useAuth";

// ─── Types ────────────────────────────────────────────────────────────────────

type PeriodFilter = "week" | "month" | "3months" | "current" | "last" | "year" | "lastyear" | "custom";

// ─── Period helpers ───────────────────────────────────────────────────────────

const periodOptions: { id: PeriodFilter; label: string }[] = [
  { id: "week", label: "This Week" },
  { id: "month", label: "This Month" },
  { id: "3months", label: "3 Months" },
  { id: "current", label: "Current Month" },
  { id: "last", label: "Last Month" },
  { id: "year", label: "This Year" },
  { id: "lastyear", label: "Last Year" },
  { id: "custom", label: "Custom" },
];

function getPeriodDates(period: PeriodFilter, customStart: string, customEnd: string): { from: string; to: string } {
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  if (period === "custom") return { from: customStart, to: customEnd };

  const start = new Date(today);
  if (period === "week") { start.setDate(today.getDate() - 7); }
  else if (period === "month" || period === "current") { start.setDate(1); }
  else if (period === "last") { start.setMonth(today.getMonth() - 1, 1); today.setDate(0); }
  else if (period === "3months") { start.setMonth(today.getMonth() - 3); }
  else if (period === "year") { start.setMonth(0, 1); }
  else if (period === "lastyear") { start.setFullYear(today.getFullYear() - 1, 0, 1); today.setFullYear(today.getFullYear() - 1, 11, 31); }

  return { from: iso(start), to: iso(today) };
}

// ─── Navigation tabs ──────────────────────────────────────────────────────────

const navTabs = [
  { id: "performance", label: "Performance", href: "/reports/vendors/performance" },
  { id: "assignments", label: "Assignments", href: "/reports/vendors/assignments" },
  { id: "commissions", label: "Commissions", href: "/reports/vendors/commissions" },
  { id: "comparison", label: "Comparison", href: "/reports/vendors/comparison" },
];

// ─── Layout Props ─────────────────────────────────────────────────────────────

interface VendorReportsLayoutProps {
  children: React.ReactNode;
  onPeriodChange?: (period: { from: string; to: string }) => void;
}

// ─── Layout Component ─────────────────────────────────────────────────────────

export default function VendorReportsLayout({ children, onPeriodChange }: VendorReportsLayoutProps) {
  const { user } = useAuth();
  const isStaff = user?.role === 'staff';
  const location = useLocation();
  
  const [activePeriod, setActivePeriod] = useState<PeriodFilter>("month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [appliedPeriod, setAppliedPeriod] = useState<PeriodFilter>("month");
  const [appliedCustomStart, _setAppliedCustomStart] = useState("");
  const [appliedCustomEnd, _setAppliedCustomEnd] = useState("");

  // Determine active tab from pathname
  const activeTab = navTabs.find(tab => location.pathname?.includes(tab.id))?.id || "performance";

  // Apply period filter
  const handleApplyPeriod = () => {
    const dates = getPeriodDates(appliedPeriod, appliedCustomStart, appliedCustomEnd);
    onPeriodChange?.(dates);
  };

  // Quick-apply when clicking non-custom period buttons
  const handlePeriodClick = (id: PeriodFilter) => {
    setActivePeriod(id);
    if (id !== "custom") {
      setAppliedPeriod(id);
      const dates = getPeriodDates(id, "", "");
      onPeriodChange?.(dates);
    }
  };

  // Staff cannot see financial data
  const filteredTabs = isStaff 
    ? navTabs.filter(tab => tab.id !== "commissions")
    : navTabs;

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-5">
        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-stone-800 font-serif">Vendor Reports</h2>
            <p className="text-xs text-stone-400">Comprehensive vendor analytics and insights</p>
          </div>
        </div>

        {/* ── Navigation Tabs ── */}
        <div className="flex gap-2 border-b border-stone-200">
          {filteredTabs.map((tab) => (
            <Link
              key={tab.id}
              to={tab.href}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-amber-500 text-amber-700"
                  : "border-transparent text-stone-500 hover:text-stone-700 hover:border-stone-300"
              }`}
            >
              {tab.label}
            </Link>
          ))}
        </div>

        {/* ── Period Filters ── */}
        <div className="flex flex-wrap gap-2">
          {periodOptions.map((p) => (
            <button
              key={p.id}
              onClick={() => handlePeriodClick(p.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                activePeriod === p.id
                  ? "bg-amber-50 border-amber-300 text-amber-700"
                  : "bg-white border-stone-200 text-stone-600 hover:border-stone-300"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {activePeriod === "custom" && (
          <div className="flex flex-wrap items-center gap-3 p-3 bg-stone-50 rounded-xl border border-stone-200">
            <span className="text-xs text-stone-500">From:</span>
            <input 
              type="date" 
              value={customStart} 
              onChange={(e) => setCustomStart(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-stone-200 text-sm focus:border-amber-400 outline-none" 
            />
            <span className="text-xs text-stone-500">To:</span>
            <input 
              type="date" 
              value={customEnd} 
              onChange={(e) => setCustomEnd(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-stone-200 text-sm focus:border-amber-400 outline-none" 
            />
            <button 
              onClick={handleApplyPeriod}
              className="px-4 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-medium hover:bg-amber-600 transition-colors"
            >
              Apply
            </button>
          </div>
        )}

        {/* ── Content ── */}
        {children}
      </div>
    </DashboardLayout>
  );
}
